// Cari kartı (yeni / düzenle). Durum ve kayıt mantığı useAccountCardForm'da; bu dosya yalnız yerleşimi kurar:
// üstte kimlik şeridi, solda bölümler, sağda cari özeti, altta sabit kaydet çubuğu. Stok kartıyla aynı düzen.
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Snackbar, Tab,
  Tabs, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import AccountGroupModal from './AccountGroupModal';
import GuideButton from '../../common/guide/GuideButton';
import { useAccountCardForm, type AddAccountProps, type AccountProblem } from './accountCard/useAccountCardForm';
import { IdentityStrip, PersonTypeToggle, TYPE_HINT_SKIP } from './accountCard/sections/IdentityStrip';
import { TaxInfoSection } from './accountCard/sections/TaxInfoSection';
import { ContactSection } from './accountCard/sections/ContactSection';
import { RiskSection } from './accountCard/sections/RiskSection';
import { EcommerceSection } from './accountCard/sections/EcommerceSection';
import { OtherSection } from './accountCard/sections/OtherSection';
import { SummaryPanel } from './accountCard/sections/SummaryPanel';
import { NoIntegrationDialog } from './accountCard/sections/NoIntegrationDialog';

type SectionKey = 'taxinfo' | 'contact' | 'risk' | 'ecommerce' | 'other';

const L = 'current.modals.addAccount';

// Eksik alan kimlik şeridindeyse odaklanacak alan
const IDENTITY_FOCUS = ['account-card-tax', 'account-card-name', 'account-card-group'];

const AddAccount: React.FC<AddAccountProps> = (props) => {
  const f = useAccountCardForm(props);
  const { t } = useTranslation('accounting');
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));
  const [active, setActive] = useState<SectionKey>('taxinfo');
  // Kişi türü seçilmeden kartta başka yere tıklanınca başlıktaki seçime yönlendiren geçici uyarı
  const [typeHint, setTypeHint] = useState(false);
  const typeHintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hideTypeHint = () => {
    if (typeHintTimer.current) clearTimeout(typeHintTimer.current);
    typeHintTimer.current = null;
    setTypeHint(false);
  };

  const showTypeHintIfNeeded = (e: React.SyntheticEvent) => {
    if (f.accountData.personType || f.initialLoading) return;
    const target = e.target as HTMLElement | null;
    // Rehber gibi portal pencereleri React ağacında buradan geçer; kartın DOM'u dışındaki tıklamalar sayılmaz.
    if (!target || !(e.currentTarget as HTMLElement).contains(target)) return;
    if (target.closest?.(`[${TYPE_HINT_SKIP}]`)) return;
    setTypeHint(true);
    if (typeHintTimer.current) clearTimeout(typeHintTimer.current);
    typeHintTimer.current = setTimeout(() => setTypeHint(false), 4000);
  };

  // Tür seçilince ya da pencere kapanınca uyarı kalkar
  useEffect(() => {
    if (f.accountData.personType || !f.open) hideTypeHint();
  }, [f.accountData.personType, f.open]);

  useEffect(() => () => { if (typeHintTimer.current) clearTimeout(typeHintTimer.current); }, []);

  useEffect(() => { if (f.open) setActive('taxinfo'); }, [f.open]);

  const sections: { key: SectionKey; label: string }[] = [
    { key: 'taxinfo', label: t(`${L}.layout.sections.taxinfo`, 'Kimlik ve vergi') },
    { key: 'contact', label: t(`${L}.layout.sections.contact`, 'İletişim ve adres') },
    { key: 'risk', label: t(`${L}.layout.sections.risk`, 'Vade ve risk') },
    { key: 'ecommerce', label: t(`${L}.layout.sections.ecommerce`, 'E-ticaret / B2B') },
    { key: 'other', label: t(`${L}.layout.sections.other`, 'Şube ve notlar') },
  ];

  const sectionHasProblem = (key: string) => f.problems.some(p => p.section === key);

  // Yeşil nokta: bölümde varsayılanın dışında bilgi girilmiş
  const d = f.accountData;
  const filled: Record<SectionKey, boolean> = {
    taxinfo: !!(d.taxOffice.trim() || d.tradeRegisterNumber.trim() || d.authorizedPerson.trim() || d.authorizedPersonPhone.trim()),
    contact: !!(d.phone.trim() || d.mobilePhone.trim() || d.email.trim() || d.address.trim() || d.city.trim() || d.district.trim()),
    risk: (d.riskLimitAmount !== '' && d.riskLimitAmount !== '0') || d.preventNegativeBalance || d.paymentDueDays !== '30',
    ecommerce: d.isEcommerceUser || d.isB2BDealer || !!d.customerRepresentative.trim(),
    other: !!(f.selectedBranch || d.notes.trim()),
  };

  // Alt çubuk: eksiklerin hangi bölümde olduğu
  const sectionName = (key: AccountProblem['section']) => sections.find(s => s.key === key)?.label ?? key;
  const missingParts = Array.from(new Set(f.problems.map(p => p.section))).map(key => ({
    key,
    count: f.problems.filter(p => p.section === key).length,
  }));
  const missingText = missingParts
    .map(m => (m.key === 'identity'
      ? t(`${L}.layout.missingInStrip`, 'Üst şeritte {{count}} zorunlu alan eksik', { count: m.count })
      : t(`${L}.layout.missingIn`, '{{section}} bölümünde {{count}} zorunlu alan eksik', { section: sectionName(m.key), count: m.count })))
    .join(' · ');

  const goTo = (section: AccountProblem['section']) => {
    if (section === 'identity') {
      const d = f.accountData;
      const target = !d.taxNumber && f.isLegalPerson ? IDENTITY_FOCUS[0]
        : !d.companyName.trim() ? IDENTITY_FOCUS[1]
          : !d.groupId ? IDENTITY_FOCUS[2]
            : IDENTITY_FOCUS[0];
      document.getElementById(target)?.focus();
      return;
    }
    setActive(section);
  };

  const onSave = (keepOpenForNext: boolean) => {
    if (f.problems.length > 0) {
      f.setError(f.problems.map(p => p.message).join(' · '));
      goTo(f.problems[0].section);
      return;
    }
    f.handleSubmit(keepOpenForNext);
  };

  const pane = (key: SectionKey, node: React.ReactNode) => (
    <Box key={key} role="tabpanel" id={`account-card-pane-${key}`} hidden={active !== key} sx={{ minWidth: 0 }}>
      {node}
    </Box>
  );

  const summary = <SummaryPanel f={f} onGoTo={goTo} />;

  return (
    <>
      <Dialog
        open={f.open}
        onClose={f.handleClose}
        fullScreen={!isDesktop}
        maxWidth={false}
        PaperProps={{
          onMouseDownCapture: showTypeHintIfNeeded,
          onFocusCapture: showTypeHintIfNeeded,
          sx: isDesktop
            ? { width: 'min(1180px, 96vw)', height: 'min(800px, 94vh)', borderRadius: 2, display: 'flex', flexDirection: 'column' }
            : { display: 'flex', flexDirection: 'column' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, py: 1.5, px: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 700 }}>
            {f.isEditMode ? t(`${L}.titleEdit`) : t(`${L}.titleAdd`)}
          </Typography>
          <PersonTypeToggle f={f} hint={typeHint} onHintClose={hideTypeHint} />
          <GuideButton guideKey="accounts.add" {...{ [TYPE_HINT_SKIP]: 'true' }} />
          <IconButton {...{ [TYPE_HINT_SKIP]: 'true' }} edge="end" onClick={f.handleClose} aria-label={t('current.actions.close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {f.initialLoading ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
        ) : (
          <>
            <Box sx={{ px: 2.5, py: 2, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
              <IdentityStrip f={f} />
            </Box>

            {!isDesktop && (
              <Tabs
                value={active}
                onChange={(_, v) => setActive(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 40 }}
              >
                {sections.map(s => (
                  <Tab
                    key={s.key}
                    value={s.key}
                    sx={{ minHeight: 40, textTransform: 'none' }}
                    label={sectionHasProblem(s.key) ? `${s.label} ●` : s.label}
                  />
                ))}
              </Tabs>
            )}

            <DialogContent sx={{ p: 0, flex: 1, display: 'flex', minHeight: 0 }}>
              {isDesktop && (
                <Box
                  component="nav"
                  aria-label={t(`${L}.layout.sectionsNav`, 'Kart bölümleri')}
                  sx={{ width: 200, flex: 'none', borderRight: '1px solid', borderColor: 'divider', py: 1.5, px: 1, overflowY: 'auto' }}
                >
                  <Box role="tablist" aria-orientation="vertical" sx={{ display: 'grid', gap: 0.25 }}>
                    {sections.map(s => {
                      const selected = s.key === active;
                      return (
                        <Box
                          key={s.key}
                          component="button"
                          type="button"
                          role="tab"
                          aria-selected={selected}
                          aria-controls={`account-card-pane-${s.key}`}
                          onClick={() => setActive(s.key)}
                          sx={{
                            all: 'unset',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            fontSize: '0.9rem',
                            fontWeight: selected ? 600 : 400,
                            color: selected ? 'primary.main' : 'text.primary',
                            bgcolor: selected ? 'action.selected' : 'transparent',
                            '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                          }}
                        >
                          <span>{s.label}</span>
                          {(sectionHasProblem(s.key) || filled[s.key]) && (
                            <Box
                              component="span"
                              title={sectionHasProblem(s.key)
                                ? t(`${L}.layout.sectionHasMissing`, 'Eksik zorunlu alan var')
                                : t(`${L}.layout.sectionFilled`, 'Dolu')}
                              sx={{
                                width: 8, height: 8, borderRadius: '50%', flex: 'none',
                                bgcolor: sectionHasProblem(s.key) ? 'error.main' : 'success.main',
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, mt: 1.5, lineHeight: 1.5 }}>
                    {t(`${L}.layout.dotLegend`, 'Kırmızı nokta: zorunlu alan eksik. Yeşil: bölüm dolu.')}
                  </Typography>
                </Box>
              )}

              <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2.5 }}>
                {f.error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => f.setError(null)}>{f.error}</Alert>
                )}
                {pane('taxinfo', (
                  <>
                    <TaxInfoSection f={f} />
                    {!isWide && <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>{summary}</Paper>}
                  </>
                ))}
                {pane('contact', <ContactSection f={f} />)}
                {pane('risk', <RiskSection f={f} />)}
                {pane('ecommerce', <EcommerceSection f={f} />)}
                {pane('other', <OtherSection f={f} />)}
              </Box>

              {isWide && (
                <Box
                  component="aside"
                  aria-label={t(`${L}.layout.summary`, 'Cari özeti')}
                  sx={{ width: 260, flex: 'none', borderLeft: '1px solid', borderColor: 'divider', p: 2, overflowY: 'auto', bgcolor: 'action.hover' }}
                >
                  {summary}
                </Box>
              )}
            </DialogContent>
          </>
        )}

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            gap: 1,
            flexWrap: 'wrap',
            pb: { xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', md: 1.5 },
          }}
        >
          <Typography
            variant="body2"
            color={f.problems.length > 0 ? 'error' : 'text.secondary'}
            sx={{ flex: 1, minWidth: 160 }}
          >
            {f.problems.length > 0 ? `● ${missingText}` : ''}
          </Typography>
          <Button {...{ [TYPE_HINT_SKIP]: 'true' }} onClick={f.handleClose} variant="outlined" size="small" disabled={f.loading}>
            {t(`${L}.layout.discard`, 'Vazgeç')}
          </Button>
          {!f.isEditMode && !f.isPrefill && (
            <Button onClick={() => onSave(true)} variant="outlined" size="small" disabled={f.loading || f.initialLoading}>
              {t(`${L}.layout.saveAndNew`, 'Kaydet ve yeni ekle')}
            </Button>
          )}
          <Button onClick={() => onSave(false)} variant="contained" size="small" disabled={f.loading || f.initialLoading} sx={{ minWidth: 110 }}>
            {f.loading ? (
              <>
                <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                {f.isEditMode ? t(`${L}.updating`) : t(`${L}.saving`)}
              </>
            ) : (
              f.isEditMode ? t(`${L}.update`) : t(`${L}.save`)
            )}
          </Button>
        </DialogActions>

        <AccountGroupModal open={f.isGroupModalOpen} onClose={f.handleCloseGroupModal} />
      </Dialog>

      <Snackbar
        open={!!f.successMsg}
        autoHideDuration={3500}
        onClose={() => f.setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: (th) => th.zIndex.modal + 10 }}
      >
        <Alert onClose={() => f.setSuccessMsg(null)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {f.successMsg}
        </Alert>
      </Snackbar>
      <NoIntegrationDialog open={f.noIntegrationOpen} onClose={() => f.setNoIntegrationOpen(false)} />
    </>
  );
};

export default AddAccount;
